import { useNooks, useSearchStream, type NookKind, type NookListItem } from "@nook/api-client"
import { nookHost, nookTitle, SourceIcon } from "@nook/client-ui"
import { Button, cn, Input, SidebarTrigger, ToggleGroup, ToggleGroupItem } from "@nook/ui"
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router"
import { LayoutGrid, Plus, Rows3, Sparkles, SquarePen } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useState } from "react"

import { useOverlay } from "@/components/app-shell.js"
import { BrowseFilters } from "@/components/browse-filters.js"
import { useKeys } from "@/components/keys.js"
import { NookGrid } from "@/components/nook-grid.js"
import { ConfirmDelete, NookPanel } from "@/components/nook-panel.js"
import { isNote } from "@/components/note-editor.js"
import {
  browseFilters,
  parseBrowseSearch,
  useLibraryView,
  type BrowseSearch,
  type LibraryView,
} from "@/lib/browse.js"
import { useReader } from "@/lib/reader.js"

export const Route = createFileRoute("/_authed/")({
  validateSearch: parseBrowseSearch,
  component: () => <Browse />,
})

/**
 * The library (block 03), browsable (block 19), driven by the keyboard.
 *
 * `?nook=` is the open nook, so a refresh or a pasted link reopens it. It slides
 * over the library from the right, the library staying put underneath with the
 * open nook marked, and pages through the list in the order on screen. No plan
 * gate: browse works on every plan state including expired (PRD §10.2).
 */
export function Browse({ kind }: { kind?: NookKind.Note }) {
  const search: BrowseSearch = useSearch({ strict: false })
  const navigate = useNavigate()
  const overlay = useOverlay()
  const [view, setView] = useLibraryView()
  const [reader] = useReader()
  const filters = browseFilters(search, kind)
  const nooks = useNooks(filters)
  const items =
    nooks.data?.pages.flatMap((page) =>
      page.items.filter((item: NookListItem) => !item.optimistic),
    ) ?? []
  const ids = items.map((item) => item.id)
  const at = search.nook ? ids.indexOf(search.nook) : -1
  // `null` until the keyboard moves: a pointer user never sees a cursor ring.
  const [cursor, setCursor] = useState<number | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const current =
    cursor === null ? undefined : items[Math.min(cursor, items.length - 1)]
  const reduce = useReducedMotion()
  const { hasNextPage, isFetching, fetchNextPage } = nooks

  // Paging up to the last loaded nook pulls the next page, so J keeps going.
  useEffect(() => {
    if (at >= 0 && at >= ids.length - 2 && hasNextPage && !isFetching)
      void fetchNextPage()
  }, [at, ids.length, hasNextPage, isFetching, fetchNextPage])

  // Closing a nook leaves the cursor on it.
  useEffect(() => {
    if (at >= 0) setCursor(at)
  }, [at])

  // Stepping replaces the entry, so Back leaves the nook rather than walking it.
  const open = (nook: string | undefined, replace: boolean) =>
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, nook }),
      replace,
      resetScroll: false,
    })

  const move = (by: number) =>
    setCursor((at) =>
      at === null ? 0 : Math.max(0, Math.min(items.length - 1, at + by)),
    )
  // Whichever layout the breakpoint shows; phones only get the grid.
  const grid = () =>
    [...document.querySelectorAll("[data-grid]")].find((el) => el.checkVisibility())
  const row = () => {
    const el = grid()
    return el ? getComputedStyle(el).gridTemplateColumns.split(" ").length : 1
  }
  const flip = () => setView(view === "grid" ? "compact" : "grid")

  useKeys(
    {
      j: () => move(1),
      k: () => move(-1),
      ArrowDown: () => move(row()),
      ArrowUp: () => move(-row()),
      ArrowRight: () => grid() && move(1),
      ArrowLeft: () => grid() && move(-1),
      Enter: () =>
        current &&
        (isNote(current)
          ? void navigate({ to: "/n/$id", params: { id: current.id } })
          : open(current.id, false)),
      o: () =>
        current?.sourceUrl && window.open(current.sourceUrl, "_blank", "noopener"),
      "$mod+Backspace": () => current && setDeleting(current.id),
      v: flip,
    },
    !search.nook,
  )

  return (
    <div className="relative h-full">
      <div className="h-full overflow-y-auto">
        {/* Below md the filters drop to a second row. The library scrolls under it. */}
        <header className="glass sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b px-4 py-3 md:h-14 md:flex-nowrap md:px-6 md:py-0">
          <SidebarTrigger className="-ml-2 md:hidden" />
          <h1 className="mr-3 text-xl font-semibold tracking-tight">
            {kind ? "Notes" : "Library"}
          </h1>
          <div className="order-last w-full md:order-none md:w-auto">
            <BrowseFilters sources={!kind} />
          </div>
          <AskBar onOpen={(id) => open(id, false)} />
          <span className="flex-1" />
          <Button
            variant="outline"
            size="icon-sm"
            className="size-7.5 md:hidden"
            aria-label="Ask Nook"
            asChild
          >
            <Link to="/ask/$threadId" params={{ threadId: "new" }}>
              <Sparkles />
            </Link>
          </Button>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={view}
            onValueChange={(value) => value && setView(value as LibraryView)}
            className="max-md:hidden"
          >
            <ToggleGroupItem value="grid" aria-label="Grid view" className="h-7 w-8">
              <LayoutGrid />
            </ToggleGroupItem>
            <ToggleGroupItem value="compact" aria-label="List view" className="h-7 w-8">
              <Rows3 />
            </ToggleGroupItem>
          </ToggleGroup>
          {kind ? (
            <Button
              size="sm"
              aria-label="New note"
              className="h-7.5 gap-2 text-[0.8125rem] max-md:w-7.5 max-md:px-0 md:ml-2 md:pr-1.5 md:pl-2.5"
              asChild
            >
              <Link to="/n/$id" params={{ id: "new" }}>
                <SquarePen />
                <span className="max-md:hidden">New note</span>
              </Link>
            </Button>
          ) : (
            <Button
              size="sm"
              aria-label="New nook"
              className="h-7.5 gap-2 text-[0.8125rem] max-md:w-7.5 max-md:px-0 md:ml-2 md:pr-1.5 md:pl-2.5"
              onClick={() => overlay("capture")}
            >
              <Plus />
              <span className="max-md:hidden">New nook</span>
            </Button>
          )}
        </header>

        <div>
          {view === "grid" ? (
            <NookGrid
              filters={filters}
              view="grid"
              selected={search.nook ?? current?.id}
            />
          ) : (
            <>
              <div className="md:hidden">
                <NookGrid
                  filters={filters}
                  view="grid"
                  selected={search.nook ?? current?.id}
                />
              </div>
              <div className="max-md:hidden">
                <NookGrid
                  filters={filters}
                  view={view}
                  selected={search.nook ?? current?.id}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Over the library, not instead of it: in from the right, out the same way. */}
      <AnimatePresence>
        {search.nook ? (
          <motion.div
            key="panel"
            className={cn(
              "bg-background absolute inset-y-0 right-0 z-20 flex w-full border-l shadow-2xl transition-[width] duration-300",
              !reader.wide && "md:w-[min(680px,62%)]",
            )}
            initial={reduce ? { opacity: 0 } : { x: "100%" }}
            animate={reduce ? { opacity: 1 } : { x: 0 }}
            exit={reduce ? { opacity: 0 } : { x: "100%" }}
            transition={{ type: "spring", bounce: 0, duration: 0.35 }}
          >
            <NookPanel
              id={search.nook}
              ids={ids}
              expandable
              onOpen={(id) => open(id, true)}
              onClose={() => open(undefined, false)}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <ConfirmDelete id={deleting} onDone={() => setDeleting(null)} />
    </div>
  )
}

/** Suggests nooks while typing; a click opens one, ↵ asks the question. */
function AskBar({ onOpen }: { onOpen: (id: string) => void }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState("")
  const [focused, setFocused] = useState(false)
  const trimmed = query.trim()
  const { results } = useSearchStream(trimmed, 5)

  return (
    <form
      className="relative ml-3 hidden max-w-[480px] min-w-0 flex-1 md:block"
      onSubmit={(event) => {
        event.preventDefault()
        if (trimmed)
          void navigate({
            to: "/ask/$threadId",
            params: { threadId: "new" },
            search: { q: trimmed },
          })
      }}
    >
      <Sparkles className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
      <Input
        aria-label="Ask about your nooks"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return
          setQuery("")
          event.currentTarget.blur()
        }}
        placeholder="Ask about your nooks…"
        className="h-7.5 pl-8 text-[0.8125rem]"
      />
      {focused && trimmed && results.length ? (
        // Keeps focus in the input, so the click lands before the list closes.
        <div
          className="bg-popover text-popover-foreground absolute inset-x-0 top-full z-50 mt-1 rounded-md border p-1 shadow-md"
          onMouseDown={(event) => event.preventDefault()}
        >
          {results.map((nook) => (
            <button
              key={nook.id}
              type="button"
              onClick={() => onOpen(nook.id)}
              className="hover:bg-accent flex h-8.5 w-full items-center gap-3 rounded-sm px-2 text-left text-sm"
            >
              <SourceIcon icon={nook.sourceIcon} />
              <span className="flex-1 truncate">{nookTitle(nook)}</span>
              <span className="text-muted-foreground text-xs">{nookHost(nook)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </form>
  )
}
